import { Loader2 } from "lucide-react";
import { FolderX } from "lucide-react";
import { Link, useParams } from "react-router";
import { EmptyState } from "@/components/gb/empty-state";
import { Button } from "@/components/ui/button";
import { ProjectNotFoundError, useProject } from "@/features/project/api";
import { AppShell, ContentColumn, WorkspaceChrome } from "./app-shell";
import { ProjectContext } from "./project-context";

/**
 * Resolves `/projects/:projectId` into a project, once, for everything below it.
 *
 * This is where the four states live — pending, not-found, failed, loaded.
 * `ProjectScreen`, `SetupWizard` and `SetupEntry` each used to implement their
 * own copy of them against `useCurrentProject()`; hoisting them here means one
 * implementation and, more usefully, a **non-nullable** project everywhere
 * below. That is what let `useProjectName()` drop its "No project" fallback.
 *
 * Note this does not break the "loading and error belong at the call site, not
 * inside hooks" convention that the rest of the data layer follows: this is a
 * component, and it is the call site. It is simply a call site that five
 * screens sit inside.
 */
export function ProjectLayout() {
  const { projectId } = useParams();
  const { data: project, isPending, error, refetch } = useProject(projectId);

  if (isPending) {
    return (
      <Shell>
        <div
          className="flex items-center gap-2 py-16 text-muted"
          role="status"
          aria-live="polite"
        >
          <Loader2 size={16} className="animate-spin" />
          <span className="text-[0.9rem]">Opening project…</span>
        </div>
      </Shell>
    );
  }

  if (error) {
    // A wrong id and a broken request are different problems and get different
    // screens: one is a dead link, the other is worth retrying.
    //
    // Deliberately NOT a redirect to the picker. A silent bounce makes a stale
    // link indistinguishable from a working one, and throws away the URL the
    // user was trying to open — which, for a link pasted out of an email, was
    // the entire context.
    const notFound = error instanceof ProjectNotFoundError;
    return (
      <Shell>
        {notFound ? (
          <EmptyState
            icon={FolderX}
            title="Project not found"
            description="It may have been deleted, or the link may be for a different workspace."
            action={
              <Button asChild>
                <Link to="/">Back to projects</Link>
              </Button>
            }
          />
        ) : (
          <div className="rounded-card-lg border-[1.5px] border-danger/40 bg-danger/5 p-5">
            <p className="text-[0.9rem] font-semibold text-ink">
              Could not open this project.
            </p>
            <p className="mt-1 text-[0.85rem] text-muted">{error.message}</p>
            <Button className="mt-4" variant="secondary" size="sm" onClick={() => void refetch()}>
              Try again
            </Button>
          </div>
        )}
      </Shell>
    );
  }

  return (
    <ProjectContext.Provider value={{ project, projectId: project.id }}>
      <AppShell />
    </ProjectContext.Provider>
  );
}

/**
 * Chrome for the pre-resolution states.
 *
 * `AppShell` cannot be used here: its topbar reads the project from context,
 * which is precisely what does not exist yet. `WorkspaceChrome` is the honest
 * frame for "we do not know which project this is" — and it is real chrome, so
 * opening a project no longer flashes an unstyled column, and "Project not
 * found" keeps the rail and the sign-out control instead of stranding the user
 * on a bare page with one button.
 */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <WorkspaceChrome>
      <ContentColumn>{children}</ContentColumn>
    </WorkspaceChrome>
  );
}
